import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core'
import { ShareOptions } from 'src/app/interfaces/main'
import { QuizReportType } from 'src/app/interfaces/quiz'
import { GeneralService } from 'src/app/services/general/general.service'
import { LocaleService } from 'src/app/services/locale/locale-service'
import { LogService } from 'src/app/services/log/log.service'
import { WebviewService } from 'src/app/services/webview/webview.service'
import { KakaoShareData } from '../../../interfaces/kakao'
import { KakaoService } from '../../../services/kakao/kakao.service'
import { ModalService } from '../../../services/modal/modal.service'
import { SnackbarService } from '../../../services/snackbar/snackbar.service'
import { QrCodeModalComponent } from '../../modals/qr-code-modal/qr-code-modal.component'
import { SubShareComponent } from '../../modals/sub-share/sub-share.component'
import { LogEvent } from './../../../interfaces/log'

@Component({
  selector: 'app-kakao-share',
  templateUrl: './kakao-share.component.html',
  styleUrls: ['./kakao-share.component.scss'],
})
export class KakaoShareComponent implements OnChanges {
  constructor(
    private snackbarService: SnackbarService,
    private kakaoService: KakaoService,
    private modalService: ModalService,
    private logService: LogService,
    private webviewService: WebviewService,
    private generalService: GeneralService,
    private localeService: LocaleService
  ) {}

  @Input() quizType: QuizReportType | undefined
  @Input() totalReplyCount: number | undefined
  @Input() shareURL: string | undefined
  @Input() shareURLBase: string | undefined
  @Input() showLoading = false
  @Input() subShareModalText: string | undefined
  @Input() shareTemplateData: KakaoShareData | undefined
  @Input() shareButtonText = '카톡으로 퀴즈 공유하기'
  @Input() disabled = false
  @Input() showQrCode = false
  @Input() qrCodeTitle = ''
  @Input() qrCodeDescription = ''
  @Input() qrBorderColor = ''
  @Input() size: 'small' | 'default' = 'default'
  @Input() onlyLink: boolean = false

  @Output() logLinkCopy = new EventEmitter<void>()
  @Output() logKakaoShare = new EventEmitter<void>()
  @Output() logQrCode = new EventEmitter<void>()
  @Output() showLoadingChange = new EventEmitter<boolean>()

  quizReportType = QuizReportType
  isKo = this.localeService.isKo
  private kakaoShareLog: KakaoShareLog | undefined

  ngOnChanges(changes: SimpleChanges): void {
    if (this.quizType !== undefined) {
      this.kakaoShareLog = this.shareLogFactory()
    }
  }

  async copyUrl(): Promise<void> {
    if (this.disabled) return
    if (this.shareTemplateData === undefined || this.shareTemplateData === null) {
      this.snackbarService.openSnackBar('링크 구성에 실패했습니다. 다시 시도해주세요')
      return
    }

    try {
      const copyText = this.logService.exp.appendTestGroupParams(`${this.shareURLBase}?utm_medium=copy_link`)
      if (this.webviewService.isWebView) {
        this.webviewService.postCopyClickedMessage(copyText)
      } else {
        await this.generalService.copy(copyText)
        this.snackbarService.openSnackBar('클립보드에 링크가 복사되었습니다.')
      }
    } catch (error) {
      console.error(error)
      this.snackbarService.openSnackBar('링크 복사에 실패하였습니다.')
    }
    this.logCopyClicked()
  }

  /**
   * 카카오 공유.
   * But, 실패했을 경우 (하루 30000번 초과) web share && url copy 모달 띄우기
   */
  async kakaoShare(): Promise<void> {
    if (this.disabled) return
    if (this.shareTemplateData !== undefined) {
      this.showLoading = true
      const params = {
        ...this.shareTemplateData,
        imageLinkUrl: this.logService.exp.appendTestGroupParams(this.shareTemplateData.imageLinkUrl),
      }
      try {
        const kakaoShareResult = await this.kakaoService.shareQuizUsingKakao(params)
        if (!kakaoShareResult) {
          await this.onKakaoShareError()
        }
        this.showLoading = false
      } catch (error) {
        await this.onKakaoShareError()
        this.showLoading = false
      }
      this.logShareKakaoClicked()
    }
  }

  onClickShare() {
    if (!this.shareTemplateData) {
      return
    }
    const shareOptions: ShareOptions = {
      text: this.localeService.getLocaleString(this.shareTemplateData.title),
      url: this.shareURL,
    }
    this.generalService.share(shareOptions)
    this.logService.eventLogging(LogEvent.MBTI_QUIZ_SHARE_WEB_API)
  }

  async onClickQRCode(): Promise<void> {
    this.logQRCodeClicked()
    if (this.shareURLBase) {
      const selectModal = await this.modalService.openDialog(
        QrCodeModalComponent,
        {
          title: this.qrCodeTitle,
          description: this.qrCodeDescription,
          url: `${this.shareURLBase.split('?')[0]}?utm_medium=qr_code`,
          borderColor: this.qrBorderColor,
          logModalViewed: this.logQrCodeModalViewed,
        },
        'custom-center-round-modal'
      )
      await selectModal.present()
    }
  }

  private async onKakaoShareError(): Promise<void> {
    if (this.shareTemplateData === undefined || this.shareTemplateData === null) {
      this.snackbarService.openSnackBar('링크 구성에 실패했습니다. 다시 시도해주세요')
      return
    }

    const subShareModal = await this.modalService.openBottomSheet(SubShareComponent, {
      text: this.subShareModalText,
      url: this.shareURLBase,
      quizType: this.quizType,
      additionalParams: this.logService.exp.appendTestGroupParams(''),
      totalReplyCount: this.totalReplyCount,
    })
    await subShareModal.present()
  }

  private logCopyClicked(): void {
    if (this.quizType === undefined) {
      return
    }
    this.kakaoShareLog?.logCopyClicked()
  }

  private logShareKakaoClicked(): void {
    if (this.quizType === undefined) {
      return
    }
    this.kakaoShareLog?.logShareKakaoClicked()
  }

  private logQRCodeClicked(): void {
    this.kakaoShareLog?.logQrCodeClicked()
  }
  private logQrCodeModalViewed(): void {
    if (this.quizType) {
      this.logService.qrCodeModalViewed(this.quizType)
    }
  }
  private shareLogFactory(): KakaoShareLog | undefined {
    if (this.quizType === QuizReportType.MBTI) {
      return new MbtiKakaoShareLog(this.logService, this.totalReplyCount ?? 0)
    }
    if (this.quizType === QuizReportType.FAVOUR_TOURNAMENT) {
      return new FTKakaoShareLog(this.logService, this.totalReplyCount ?? 0)
    }
    if (this.quizType === QuizReportType.THINKING_CHAIR) {
      return new ThinkingChairKakaoShareLog(this.logService)
    }
    if (this.quizType === QuizReportType.ATTACHMENT_TYPE) {
      return new AttachmentQuizKakaoShareLog(this.logService, this.totalReplyCount ?? 0)
    }
    if (this.quizType === QuizReportType.IMAGE_GAME) {
      return new ImageGameKakaoShareLog(this.logService, this.logLinkCopy, this.logKakaoShare)
    }
    if (this.quizType === QuizReportType.GUESS_ME) {
      return new GuessMeKakaoShareLog(this.logService, this.totalReplyCount ?? 0)
    }
    if (this.quizType === QuizReportType.SURVEY) {
      return new SurveyKakaoShareLog(this.logService, this.logLinkCopy, this.logKakaoShare)
    }
    if (this.quizType === QuizReportType.GROUP_CHEMI) {
      return new GroupChemiShareLog(this.logService, this.logLinkCopy, this.logKakaoShare)
    }
    if (this.quizType === QuizReportType.MBTI_IDEAL_TYPE) {
      return new IdealTypeShareLog(this.logService, this.logLinkCopy, this.logKakaoShare)
    }
    if (this.quizType === QuizReportType.IDEAL_TYPE) {
      return new IdealTypeQuizShareLog(this.logService, this.logLinkCopy, this.logKakaoShare)
    }
    if (this.quizType === QuizReportType.MBTI_BEST_FRIEND) {
      return new MbtiBestFriendShareLog(this.logService, this.logLinkCopy, this.logKakaoShare)
    }
    if (this.quizType === QuizReportType.SCHOO_IDEAL_TYPE) {
      return new SchoolIdealTypeLog(this.logService, this.logLinkCopy, this.logKakaoShare, this.logQrCode)
    }
    if (this.quizType === QuizReportType.DAILY_LOG_QUIZ) {
      return new DailyLogQuizLog(this.logService)
    }
  }
}

abstract class KakaoShareLog {
  constructor(protected logService: LogService) {}

  abstract logCopyClicked(): void

  abstract logShareKakaoClicked(): void

  abstract logQrCodeClicked(): void
}

class MbtiKakaoShareLog extends KakaoShareLog {
  constructor(protected logService: LogService, private totalReplyCount: number) {
    super(logService)
  }

  logCopyClicked(): void {
    const event = LogEvent.MBTI_QUIZ_SHARE_COPY
    const eventParams = {
      reply_on_this_quiz: this.totalReplyCount,
      category: 'mbti_quiz_host_quiz',
    }
    this.logService.eventLogging(event, eventParams)
  }

  logShareKakaoClicked(): void {
    const event = LogEvent.MBTI_QUIZ_SHARE_KAKAO
    const eventParams = {
      reply_on_this_quiz: this.totalReplyCount,
      category: 'mbti_quiz_host_quiz',
    }
    this.logService.eventLogging(event, eventParams)
  }

  logQrCodeClicked(): void {
    const event = LogEvent.MBTI_QUIZ_SHARE_QR_CODE
    this.logService.eventLogging(event)
  }
}

class FTKakaoShareLog extends KakaoShareLog {
  constructor(protected logService: LogService, private totalReplyCount: number) {
    super(logService)
  }

  logCopyClicked(): void {
    const event = LogEvent.FW_QUIZ_SHARE_COPY
    const eventParams = {
      reply_on_this_quiz: this.totalReplyCount,
      category: 'fw_host_quiz',
    }
    this.logService.eventLogging(event, eventParams)
  }

  logShareKakaoClicked(): void {
    const event = LogEvent.FW_QUIZ_SHARE_KAKAO
    const eventParams = {
      reply_on_this_quiz: this.totalReplyCount,
      category: 'fw_host_quiz',
    }
    this.logService.eventLogging(event, eventParams)
  }

  logQrCodeClicked(): void {}
}

class ThinkingChairKakaoShareLog extends KakaoShareLog {
  constructor(protected logService: LogService) {
    super(logService)
  }

  logCopyClicked(): void {
    this.logService.tc.thinkingChairHostCreateShareURLClicked()
  }

  logShareKakaoClicked(): void {
    this.logService.tc.thinkingChairHostCreateShareKakaoClicked()
  }

  logQrCodeClicked(): void {}
}

class AttachmentQuizKakaoShareLog extends KakaoShareLog {
  constructor(protected logService: LogService, private totalReplyCount: number) {
    super(logService)
  }

  logCopyClicked(): void {
    this.logService.attachment.logAttachmentQuizHostQuizDetailShareURLClicked(this.totalReplyCount)
  }

  logShareKakaoClicked(): void {
    this.logService.attachment.logAttachmentQuizHostQuizDetailShareKakaoClicked(this.totalReplyCount)
  }

  logQrCodeClicked(): void {}
}

class ImageGameKakaoShareLog extends KakaoShareLog {
  constructor(
    protected logService: LogService,
    private logCopy: EventEmitter<void>,
    private logKakao: EventEmitter<void>
  ) {
    super(logService)
  }

  logCopyClicked(): void {
    this.logCopy.emit()
  }

  logShareKakaoClicked(): void {
    this.logKakao.emit()
  }

  logQrCodeClicked(): void {}
}

class GuessMeKakaoShareLog extends KakaoShareLog {
  constructor(protected logService: LogService, private totalReplyCount: number) {
    super(logService)
  }

  logCopyClicked(): void {
    this.logService.guessMe.logGuessMeHostQuizDetailShareURLClicked(this.totalReplyCount)
  }

  logShareKakaoClicked(): void {
    this.logService.guessMe.logGuessMeHostQuizDetailShareKakaoClicked(this.totalReplyCount)
  }

  logQrCodeClicked(): void {}
}

class SurveyKakaoShareLog extends KakaoShareLog {
  constructor(
    protected logService: LogService,
    private logCopy: EventEmitter<void>,
    private logKakao: EventEmitter<void>
  ) {
    super(logService)
  }

  logCopyClicked(): void {
    this.logCopy.emit()
  }

  logShareKakaoClicked(): void {
    this.logKakao.emit()
  }

  logQrCodeClicked(): void {}
}

class GroupChemiShareLog extends KakaoShareLog {
  constructor(
    protected logService: LogService,
    private logCopy: EventEmitter<void>,
    private logKakao: EventEmitter<void>
  ) {
    super(logService)
  }

  logCopyClicked(): void {
    this.logCopy.emit()
  }

  logShareKakaoClicked(): void {
    this.logKakao.emit()
  }

  logQrCodeClicked(): void {}
}

class IdealTypeShareLog extends KakaoShareLog {
  constructor(
    protected logService: LogService,
    private logCopy: EventEmitter<void>,
    private logKakao: EventEmitter<void>
  ) {
    super(logService)
  }

  logCopyClicked(): void {
    this.logService.idealType.idealTypeResultShareButtonClicked('copy')
  }

  logShareKakaoClicked(): void {
    this.logService.idealType.idealTypeResultShareButtonClicked('kakao')
  }

  logQrCodeClicked(): void {
    this.logService.idealType.idealTypeResultQrCodeButtonClicked()
  }
}

class IdealTypeQuizShareLog extends KakaoShareLog {
  constructor(
    protected logService: LogService,
    private logCopy: EventEmitter<void>,
    private logKakao: EventEmitter<void>
  ) {
    super(logService)
  }

  logCopyClicked(): void {
    this.logService.idealTypeQuiz.quizReportShareButtonClicked('copy')
  }

  logShareKakaoClicked(): void {
    this.logService.idealTypeQuiz.quizReportShareButtonClicked('kakao')
  }

  logQrCodeClicked(): void {
    this.logService.idealTypeQuiz.quizReportQrCodeButtonClicked()
  }
}

class MbtiBestFriendShareLog extends KakaoShareLog {
  constructor(
    protected logService: LogService,
    private logCopy: EventEmitter<void>,
    private logKakao: EventEmitter<void>
  ) {
    super(logService)
  }

  logCopyClicked(): void {
    this.logService.mbtiBF.bestFriendResultShareButtonClicked('copy')
  }

  logShareKakaoClicked(): void {
    this.logService.mbtiBF.bestFriendResultShareButtonClicked('kakao')
  }

  logQrCodeClicked(): void {}
}

class SchoolIdealTypeLog extends KakaoShareLog {
  constructor(
    protected logService: LogService,
    private logCopy: EventEmitter<void>,
    private logKakao: EventEmitter<void>,
    private logQr: EventEmitter<void>
  ) {
    super(logService)
  }

  logCopyClicked(): void {
    this.logCopy.emit()
  }

  logShareKakaoClicked(): void {
    // this.logService.schoolIdealType.logParticipateResultShareButtonClicked('kakao')
    this.logKakao.emit()
  }

  logQrCodeClicked(): void {
    // this.logService.schoolIdealType.logParticipateResultShareButtonClicked('qr')
    this.logQr.emit()
  }
}

class DailyLogQuizLog extends KakaoShareLog {
  constructor(protected logService: LogService) {
    super(logService)
  }

  logCopyClicked(): void {
    this.logService.logQuiz.logDailyLogQuizShareClicked('copy')
  }

  logShareKakaoClicked(): void {
    this.logService.logQuiz.logDailyLogQuizShareClicked('kakao')
  }

  logQrCodeClicked(): void {
    this.logService.logQuiz.logDailyLogQuizShareClicked('qr')
  }
}
